import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { adminPermissionsGuard } from './core/guards/admin-permissions.guard';
import { masterAdminGuard } from './core/guards/master-admin.guard';
import { withRoutePermissions } from './core/utils/route-permission.utils';

export const routes: Routes = [
  {
    path: 'login',
    pathMatch: 'full',
    redirectTo: 'auth/login'
  },
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES)
  },
  {
    // Public legal section (terms, privacy, cookies, billing, acceptable use,
    // providers). Declared before the shell route on purpose: it must be
    // reachable without a session, and reachable while signed in too, so it
    // cannot live under `canActivateChild: [authGuard]`.
    path: 'legal',
    loadChildren: () => import('./features/legal/legal.routes').then((m) => m.LEGAL_ROUTES)
  },
  {
    path: '',
    loadComponent: () =>
      import('./layout/shell/shell.component').then((m) => m.ShellComponent),
    canActivateChild: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home'
      },
      {
        path: 'home',
        loadChildren: () => import('./features/home/home.routes').then((m) => m.HOME_ROUTES)
      },
      {
        path: 'dashboard',
        data: withRoutePermissions(['DASHBOARD_PAGE']),
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then(
            (m) => m.DASHBOARD_ROUTES
          )
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./features/profile/profile.routes').then(
            (m) => m.PROFILE_ROUTES
          )
      },
      {
        path: 'users',
        data: withRoutePermissions(['USERS_GET']),
        loadChildren: () =>
          import('./features/users/users.routes').then(
            (m) => m.USERS_ROUTES
          )
      },
      {
        path: 'students',
        pathMatch: 'full',
        redirectTo: 'users'
      },
      {
        path: 'teachers/list',
        pathMatch: 'full',
        redirectTo: 'users'
      },
      {
        path: 'teachers/assignments',
        pathMatch: 'full',
        redirectTo: 'teacher-assignments'
      },
      {
        path: 'teachers/assigments',
        pathMatch: 'full',
        redirectTo: 'teacher-assignments'
      },
      {
        path: 'teachers',
        pathMatch: 'full',
        redirectTo: 'users'
      },
      {
        path: 'teacher-assignments',
        data: withRoutePermissions(['TEACHER_ASSIGNMENTS_GET']),
        loadChildren: () =>
          import('./features/teacher-assignments/teacher-assignments.routes').then(
            (m) => m.TEACHER_ASSIGNMENTS_ROUTES
          )
      },
      {
        path: 'academic-years',
        data: withRoutePermissions(['ACADEMIC_YEARS_GET']),
        loadChildren: () =>
          import('./features/academic-years/academic-years.routes').then(
            (m) => m.ACADEMIC_YEARS_ROUTES
          )
      },
      {
        path: 'student',
        data: withRoutePermissions(['studentEnrollment.view']),
        loadChildren: () =>
          import('./features/student/student.routes').then(
            (m) => m.STUDENT_ROUTES
          )
      },
      {
        path: 'courses',
        data: withRoutePermissions(['COURSES_GET']),
        loadChildren: () =>
          import('./features/courses/courses.routes').then(
            (m) => m.COURSES_ROUTES
          )
      },
      {
        // Recipient feed — every authenticated user has one.
        path: 'notifications',
        loadChildren: () =>
          import('./features/notifications/notifications.routes').then((m) => m.NOTIFICATIONS_ROUTES)
      },
      {
        path: 'notifications-management',
        data: withRoutePermissions(
          ['NOTIFICATIONS_GET', 'NOTIFICATIONS_CREATE', 'NOTIFICATIONS_EDIT'],
          'any'
        ),
        loadChildren: () =>
          import('./features/notifications/notifications-management.routes').then(
            (m) => m.NOTIFICATIONS_MANAGEMENT_ROUTES
          )
      },
      {
        path: 'calendar',
        loadChildren: () => import('./features/calendar/calendar.routes').then((m) => m.CALENDAR_ROUTES)
      },
      {
        path: 'calendar-management',
        loadChildren: () =>
          import('./features/calendar/calendar-management.routes').then((m) => m.CALENDAR_MANAGEMENT_ROUTES)
      },
      {
        path: 'grades',
        // 'any' so the assign-courses child is reachable without the grade catalog
        // page: each child still enforces its own permission.
        data: withRoutePermissions(['GRADES_PAGE', 'GRADES_ASSIGN_COURSES'], 'any'),
        loadChildren: () =>
          import('./features/grades/grades.routes').then(
            (m) => m.GRADES_ROUTES
          )
      },
      {
        path: 'sections',
        data: withRoutePermissions(['SECTIONS_GET']),
        loadChildren: () =>
          import('./features/sections/sections.routes').then(
            (m) => m.SECTIONS_ROUTES
          )
      },
      {
        path: 'section-courses',
        pathMatch: 'full',
        redirectTo: 'sections'
      },
      {
        path: 'attendance',
        data: withRoutePermissions(['STUDENT_ATTENDANCE_PAGE', 'ATTENDANCE_CALENDAR_GET', 'ATTENDANCE_EXCLUSIONS_GET'], 'any'),
        loadChildren: () => import('./features/attendance/attendance.routes').then((m) => m.ATTENDANCE_ROUTES)
      },
      {
        // Grouped under the "Notas" sidebar section, but its own top-level route:
        // the feature folder and the backend surface are both /assignments.
        path: 'assignments',
        data: withRoutePermissions(['ASSIGNMENTS_GET']),
        loadChildren: () => import('./features/assignments/assignments.routes').then((m) => m.ASSIGNMENTS_ROUTES)
      },
      {
        path: 'notes',
        data: withRoutePermissions(
          ['STUDENT_NOTES_PAGE', 'STUDENT_COURSE_ASSIGNMENT_PAGE', 'NOTES_CONFIGURATION_GET'],
          'any'
        ),
        loadChildren: () => import('./features/notes/notes.routes').then((m) => m.NOTES_ROUTES)
      },
      {
        path: 'enrollments',
        data: withRoutePermissions(['ENROLLMENTS_GET']),
        loadChildren: () =>
          import('./features/enrollments/enrollments.routes').then(
            (m) => m.ENROLLMENTS_ROUTES
          )
      },
      {
        path: 'schools',
        data: withRoutePermissions(['SCHOOLS_GET']),
        loadChildren: () =>
          import('./features/schools/schools.routes').then(
            (m) => m.SCHOOLS_ROUTES
          )
      },
      {
        path: 'employees',
        loadChildren: () =>
          import('./features/employees/employees.routes').then(
            (m) => m.EMPLOYEES_ROUTES
          )
      },
      {
        path: 'organizations',
        data: withRoutePermissions(['ORGANIZATIONS_GET']),
        loadChildren: () =>
          import('./features/organizations/organizations.routes').then(
            (m) => m.ORGANIZATIONS_ROUTES
          )
      },
      {
        path: 'subscription',
        data: withRoutePermissions(['SUBSCRIPTIONS_GET']),
        loadChildren: () =>
          import('./features/subscriptions/subscriptions.routes').then(
            (m) => m.SUBSCRIPTIONS_ROUTES
          )
      },
      {
        // Colegiaturas: the school billing the family. Distinct from
        // /subscription above, which is Tikal billing the school.
        // Deliberately UNGATED at this level, with every child carrying its own
        // permission. The block holds three very different audiences: a
        // configuration screen (TUITION_PLANS_GET), a collections screen
        // (TUITION_CHARGES_GET) and the family view, which has no permission at
        // all because the backend answers it from the caller's identity. Any
        // permission here would lock one of the three out.
        path: 'tuition',
        loadChildren: () => import('./features/tuition/tuition.routes').then((m) => m.TUITION_ROUTES)
      },
      {
        // Reports block. Each report carries its own read permission, so the
        // parent only needs any one of them to be reachable.
        path: 'reports',
        data: withRoutePermissions(['REPORTS_PENSUM_GET', 'REPORTS_NOTES_GET'], 'any'),
        loadChildren: () =>
          import('./features/reports/reports.routes').then((m) => m.REPORTS_ROUTES)
      },
      {
        path: 'roles',
        canActivate: [masterAdminGuard],
        loadChildren: () =>
          import('./features/roles/roles.routes').then((m) => m.ROLES_ROUTES)
      },
      {
        path: 'admin/permissions',
        canActivate: [adminPermissionsGuard],
        loadComponent: () =>
          import('./features/admin-permissions/admin-permissions.page').then(
            (m) => m.AdminPermissionsPageComponent
          )
      },
      {
        path: 'styles',
        canActivate: [masterAdminGuard],
        loadChildren: () => import('./features/styles/styles.routes').then((m) => m.STYLES_ROUTES)
      },
      {
        path: 'permissions',
        redirectTo: 'admin/permissions',
        pathMatch: 'full'
      },
      {
        path: 'not-found',
        loadComponent: () =>
          import('./features/not-found/not-found.page').then(
            (m) => m.NotFoundPageComponent
          )
      },
      {
        path: '**',
        redirectTo: 'not-found'
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'not-found'
  }
];
